import React, { useRef, useState, useEffect } from "react";
import { Card, Row, Col, Button, Modal, Form, Input, InputNumber, Select, Space, Divider, Badge, Tooltip, Popconfirm, List, Avatar, Empty, message, Typography, Alert, Tag } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, WarningOutlined, UnorderedListOutlined, UserOutlined, CheckCircleOutlined, UndoOutlined, FileTextOutlined, UploadOutlined } from "@ant-design/icons";
import { getBuildings, getRooms, createRoom, batchCreateRooms, updateRoom, deleteRoom, batchListRooms, batchUnlistRooms, createBuilding, getRegions, getStreets } from "../services/api";
import { ProtectedFileLink, ProtectedImage, ProtectedImagePreviewGroup } from "../components/ProtectedFile";
import { PageLoadingOverlay } from "../components/RoomLoadingScene";
import BuildingLocationPicker from "../components/BuildingLocationPicker";

const sectionTitleStyle = { fontSize: 20, fontWeight: 600, lineHeight: 1.5, margin: "0 0 16px" };

const readCachedList = key => {
    try {
        const value = JSON.parse(sessionStorage.getItem(key) || "null");
        return Array.isArray(value) ? value : null;
    } catch (error) {
        return null;
    }
};

const writeCachedList = (key, value) => {
    try { sessionStorage.setItem(key, JSON.stringify(value)); } catch (error) { /* cache is optional */ }
};

const RoomManageScreen = () => {
    let user = {};
    try { user = JSON.parse(localStorage.getItem("user") || "{}"); } catch (error) { user = {}; }
    const buildingCacheKey = `kanwuzi-buildings-${user.id || "guest"}`;
    const initialBuildingsCache = readCachedList(buildingCacheKey);
    const initialBuildings = initialBuildingsCache || [];
    const initialBuildingId = initialBuildings[0]?.id || null;
    const roomCacheKey = buildingId => `kanwuzi-rooms-${user.id || "guest"}-${buildingId}`;
    const initialRoomsCache = initialBuildingId ? readCachedList(roomCacheKey(initialBuildingId)) : null;
    const [buildings, setBuildings] = useState(initialBuildings);
    const [buildingLoading, setBuildingLoading] = useState(initialBuildingsCache === null);
    const [selectedBuilding, setSelectedBuilding] = useState(initialBuildingId);
    const [rooms, setRooms] = useState(initialRoomsCache || []);
    const [selectedRooms, setSelectedRooms] = useState([]);
    const [activeRoom, setActiveRoom] = useState(null);
    const [loading, setLoading] = useState(Boolean(initialBuildingId && initialRoomsCache === null));
    const [editModal, setEditModal] = useState(false);
    const [batchModal, setBatchModal] = useState(false);
    const [listModal, setListModal] = useState(false);
    const [buildingModal, setBuildingModal] = useState(false);
    const [creatingBuilding, setCreatingBuilding] = useState(false);
    const [savingRoom, setSavingRoom] = useState(false);
    const [creatingBatch, setCreatingBatch] = useState(false);
    const creatingBuildingRef = useRef(false);
    const savingRoomRef = useRef(false);
    const creatingBatchRef = useRef(false);
    const [rentModal, setRentModal] = useState(false);
    const [applicationModal, setApplicationModal] = useState(false);
    const [contracts, setContracts] = useState([]);
    const [contractModal, setContractModal] = useState(false);
    const [contractForm] = Form.useForm();
    const [vacantRequests, setVacantRequests] = useState([]);
    const [editingRoom, setEditingRoom] = useState(null);
    const [rentingRoom, setRentingRoom] = useState(null);
    const [form] = Form.useForm();
    const [batchForm] = Form.useForm();
    const [listForm] = Form.useForm();
    const [buildingForm] = Form.useForm();
    const [rentForm] = Form.useForm();
    const [regions, setRegions] = useState([]);
    const [streets, setStreets] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState(null);
    const [applications, setApplications] = useState([]);
    const permissions = user.permissions || { can_manage_rooms: true, can_publish: true };

    useEffect(() => { loadBuildings(); loadRegions(); loadApplications(); loadVacantRequests(); }, []);
    useEffect(() => { if (selectedBuilding) loadRooms(); }, [selectedBuilding]);
    useEffect(() => { if (selectedRegion) { loadStreets(selectedRegion); buildingForm.setFieldsValue({ street_id: undefined }); } }, [selectedRegion]);

    const loadBuildings = async () => {
        try {
            if (initialBuildingsCache === null) setBuildingLoading(true);
            const response = await getBuildings();
            const nextBuildings = Array.isArray(response) ? response : [];
            writeCachedList(buildingCacheKey, nextBuildings);
            setBuildings(nextBuildings);
            const nextBuildingId = nextBuildings.some(item => item.id === selectedBuilding) ? selectedBuilding : (nextBuildings[0]?.id || null);
            if (nextBuildingId !== selectedBuilding) {
                const cachedRooms = nextBuildingId ? readCachedList(roomCacheKey(nextBuildingId)) : [];
                setRooms(cachedRooms || []);
                setLoading(Boolean(nextBuildingId && cachedRooms === null));
                setSelectedBuilding(nextBuildingId);
            }
        } catch (error) {
            if (!initialBuildings.length) message.error("楼栋加载失败，请刷新重试");
        } finally {
            setBuildingLoading(false);
        }
    };
    const loadRooms = async () => {
        const cacheKey = roomCacheKey(selectedBuilding);
        const cachedRooms = readCachedList(cacheKey);
        try {
            if (cachedRooms) setRooms(cachedRooms);
            setLoading(cachedRooms === null);
            const response = await getRooms({ building_id: selectedBuilding });
            const nextRooms = Array.isArray(response) ? response : [];
            setRooms(nextRooms);
            writeCachedList(cacheKey, nextRooms);
            setSelectedRooms([]);
            setActiveRoom(null);
        } catch (error) {
            if (cachedRooms === null) message.error("房间加载失败，请刷新重试");
        } finally {
            setLoading(false);
        }
    };
    const loadRegions = async () => { try { const res = await getRegions(); setRegions(res); } catch (e) {} };
    const loadStreets = async (regionId) => { try { const res = await getStreets({ region_id: regionId }); setStreets(res); } catch (e) {} };
    const loadApplications = async () => {
        try { 
            const token = localStorage.getItem("token"); 
            const res = await fetch("/api/rent-applications/landlord", { 
                headers: { Authorization: "Bearer " + token } 
            }); 
            const data = await res.json(); 
            setApplications(Array.isArray(data) ? data : []); 
        } catch (e) {}
    };

    const loadVacantRequests = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await fetch("/api/vacant-requests/landlord", {
                headers: { Authorization: "Bearer " + token }
            });
            const data = await res.json();
            setVacantRequests(Array.isArray(data) ? data : []);
        } catch (e) {}
    };
    const loadContracts = async (roomId) => {
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`/api/rooms/${roomId}/contracts`, {
                headers: { Authorization: "Bearer " + token }
            });
            const data = await res.json();
            setContracts(Array.isArray(data) ? data : []);
        } catch (e) {}
    };
    const loadRoomImages = async (roomId) => {
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`/api/rooms/${roomId}/images`, {
                headers: { Authorization: "Bearer " + token }
            });
            const data = await res.json();
            setRoomImages(Array.isArray(data) ? data : []);
        } catch (e) {}
    };
    const loadRoomVideo = async (roomId) => {
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`/api/rooms/${roomId}/video`, {
                headers: { Authorization: "Bearer " + token }
            });
            const data = await res.json();
            setRoomVideo(data || null);
        } catch (e) {}
    };

    const handleVideoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !editingRoom) return;
        
        // 检查视频时长（前端限制60秒）
        if (file.size > 100 * 1024 * 1024) { // 100MB限制
            message.error("视频文件太大，请上传小于100MB的视频");
            return;
        }
        
        try {
            setVideoUploading(true);
            const token = localStorage.getItem("token");
            const formData = new FormData();
            formData.append("file", file);
            
            const res = await fetch(`/api/rooms/${editingRoom.id}/video`, {
                method: "POST",
                headers: { Authorization: "Bearer " + token },
                body: formData
            });
            
            if (res.ok) {
                message.success("视频上传成功");
                loadRoomVideo(editingRoom.id);
            } else {
                message.error("上传失败");
            }
        } catch (e) {
            message.error("上传失败");
        } finally {
            setVideoUploading(false);
        }
    };

    const handleDeleteVideo = async () => {
        try {
            const token = localStorage.getItem("token");
            await fetch(`/api/rooms/${editingRoom.id}/video`, {
                method: "DELETE",
                headers: { Authorization: "Bearer " + token }
            });
            message.success("视频删除成功");
            setRoomVideo(null);
        } catch (e) {
            message.error("删除失败");
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !editingRoom) return;
        
        try {
            setImageUploading(true);
            const token = localStorage.getItem("token");
            const formData = new FormData();
            formData.append("file", file);
            
            const res = await fetch(`/api/rooms/${editingRoom.id}/images`, {
                method: "POST",
                headers: { Authorization: "Bearer " + token },
                body: formData
            });
            
            if (res.ok) {
                message.success("图片上传成功");
                loadRoomImages(editingRoom.id);
            } else {
                message.error("上传失败");
            }
        } catch (e) {
            message.error("上传失败");
        } finally {
            setImageUploading(false);
        }
    };

    const handleDeleteImage = async (imageId) => {
        try {
            const token = localStorage.getItem("token");
            await fetch(`/api/rooms/${editingRoom.id}/images/${imageId}`, {
                method: "DELETE",
                headers: { Authorization: "Bearer " + token }
            });
            message.success("图片删除成功");
            loadRoomImages(editingRoom.id);
        } catch (e) {
            message.error("删除失败");
        }
    };
    const loadResidences = async (roomId) => {
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`/api/rooms/${roomId}/residences`, {
                headers: { Authorization: "Bearer " + token }
            });
            const data = await res.json();
            setResidences(Array.isArray(data) ? data : []);
        } catch (e) {}
    };
    const handleDeleteResidence = async (residenceId) => {
        try {
            const token = localStorage.getItem("token");
            await fetch(`/api/documents/residence/${residenceId}`, {
                method: "DELETE",
                headers: { Authorization: "Bearer " + token }
            });
            message.success("删除成功");
            loadResidences(editingRoom.id);
        } catch (e) {
            message.error("删除失败");
        }
    };

    const [fileList, setFileList] = useState([]);
    const [roomImages, setRoomImages] = useState([]);    const [roomVideo, setRoomVideo] = useState(null);
    const [videoUploading, setVideoUploading] = useState(false);
    const [imageUploading, setImageUploading] = useState(false);
    const [residences, setResidences] = useState([]);
    const [residenceModal, setResidenceModal] = useState(false);
    
    const [selectedFile, setSelectedFile] = useState(null);
    
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            console.log("选择文件:", file.name, file.type, file.size);
        }
    };
    
    const handleUploadContract = async () => {
          try {
              const title = contractForm.getFieldValue("title");
              if (!title) {
                  message.error("请输入合同标题");
                  return;
              }
              if (!editingRoom) {
                  message.error("请先选择房间");
                  return;
              }
              if (editingRoom.status !== "rented") {
                  message.error("只有已出租的房间才能上传合同");
                  return;
              }
              const token = localStorage.getItem("token");
              const formData = new FormData();
              formData.append("room_id", editingRoom.id);
              formData.append("title", title);
              if (selectedFile) {
                  formData.append("file", selectedFile);
                  console.log("添加文件:", selectedFile.name);
              } else {
                  console.log("没有文件");
              }
              console.log("上传数据:");
              for (let [key, value] of formData.entries()) {
                  console.log(key, value);
              }
              const res = await fetch("/api/contracts/upload", {
                  method: "POST",
                  headers: { Authorization: "Bearer " + token },
                  body: formData
              });
              const data = await res.json();
              if (res.ok) {
                  message.success("合同上传成功");
                  contractForm.resetFields();
                  setSelectedFile(null);
                  loadContracts(editingRoom.id);
              } else {
                  message.error(data.error || "上传失败");
              }
          } catch (e) {
              console.error("上传合同错误:", e);
              message.error("上传失败: " + e.message);
          }
      };

    const handleDeleteContract = async (contractId) => {
        try {
            const token = localStorage.getItem("token");
            await fetch(`/api/documents/contract/${contractId}`, {
                method: "DELETE",
                headers: { Authorization: "Bearer " + token }
            });
            message.success("删除成功");
            loadContracts(editingRoom.id);
        } catch (e) {
            message.error("删除失败");
        }
    };

    const handleConfirmVacant = async (requestId) => {
        try {
            const token = localStorage.getItem("token");
            await fetch("/api/vacant-requests/" + requestId + "/confirm", {
                method: "PUT",
                headers: { Authorization: "Bearer " + token }
            });
            message.success("已确认退租");
            loadVacantRequests();
            loadRooms();
        } catch (e) { message.error("操作失败"); }
    };
    const handleLandlordRequestVacant = async (roomId) => {
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`/api/rooms/${roomId}/landlord-request-vacant`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: "Bearer " + token }
            });
            const data = await res.json();
            if (res.ok) {
                message.success("退租通知已发送");
                loadVacantRequests();
            } else {
                message.error(data.error || "发送失败");
            }
        } catch (e) { message.error("操作失败"); }
    };

    const handleForceVacant = async (roomId) => {
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`/api/rooms/${roomId}/force-vacant`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: "Bearer " + token }
            });
            const data = await res.json();
            if (res.ok) {
                message.success("已强制退租");
                loadRooms();
                loadVacantRequests();
            } else {
                message.error(data.error || "操作失败");
            }
        } catch (e) { message.error("操作失败"); }
    };
    const handleApproveApplication = async (appId) => {
        try {
            const token = localStorage.getItem("token");
            await fetch("/api/rent-applications/" + appId + "/approve", {
                method: "PUT",
                headers: { Authorization: "Bearer " + token }
            });
            message.success("已同意租房申请，新入住需重新上传合同和居住登记");
            loadApplications();
            loadRooms();
        } catch (e) { message.error("操作失败"); }
    };
    const handleRejectApplication = async (appId) => {
        try {
            const token = localStorage.getItem("token");
            await fetch("/api/rent-applications/" + appId + "/reject", {
                method: "PUT",
                headers: { Authorization: "Bearer " + token }
            });
            message.success("已拒绝租房申请");
            loadApplications();
        } catch (e) { message.error("操作失败"); }
    };

    const getRoomColor = (room) => { if (room.is_listed) return "#8c8c8c"; if (room.status === "rented") return "#ff4d4f"; return "#52c41a"; };
    const getRoomTooltip = (room) => {
        if (room.status === "rented") {
            return (
                <Tooltip title="已出租">
                    <div style={{ position: "relative" }}>
                        {room.status === "rented" && (
                            <div style={{ position: "absolute", top: 2, right: 2, display: "flex", gap: 2, zIndex: 10 }}>
                                <Popconfirm title="确定要发起退租通知吗？" onConfirm={() => handleLandlordRequestVacant(room.id)}>
                                    <Button type="link" size="small" style={{ padding: 0, color: "#ff4d4f" }}>发起退租</Button>
                                </Popconfirm>
                                <Popconfirm title="确定要强制退租吗？此操作不可恢复！" onConfirm={() => handleForceVacant(room.id)}>
                                    <Button type="link" size="small" style={{ padding: 0, color: "#ff4d4f" }}>强制退租</Button>
                                </Popconfirm>
                            </div>
                        )}
                    </div>
                </Tooltip>
            );
        }
        return null;
    };
    const checkBuildingExists = () => { if (buildings.length === 0) { Modal.warning({ title: "提示", content: "请先添加楼栋！", okText: "去添加", onOk: () => { buildingForm.resetFields(); setSelectedRegion(null); setStreets([]); setBuildingModal(true); } }); return false; } return true; };

    const handleCreateBuilding = async () => {
        if (creatingBuildingRef.current) return;
        creatingBuildingRef.current = true;
        setCreatingBuilding(true);
        try {
            const values = await buildingForm.validateFields();
            const hasLatitude = values.latitude !== undefined && values.latitude !== null && values.latitude !== "";
            const hasLongitude = values.longitude !== undefined && values.longitude !== null && values.longitude !== "";
            if (hasLatitude !== hasLongitude) return message.error("请同时填写纬度和经度");
            await createBuilding(values);
            message.success("楼栋创建成功");
            setBuildingModal(false);
            buildingForm.resetFields();
            setSelectedRegion(null);
            await loadBuildings();
        } catch (e) {
            if (!e?.errorFields) message.error(e?.error || "创建失败");
        } finally {
            creatingBuildingRef.current = false;
            setCreatingBuilding(false);
        }
    };
    const handleSaveRoom = async () => {
        if (savingRoomRef.current) return;
        savingRoomRef.current = true;
        setSavingRoom(true);
        try {
            const values = await form.validateFields();
            const roomValues = { ...values, deposit_method_other: values.deposit_method === "other" ? values.deposit_method_other?.trim() : "" };
            if (editingRoom) { await updateRoom(editingRoom.id, roomValues); message.success("更新成功"); }
            else { await createRoom({ ...roomValues, building_id: selectedBuilding }); message.success("创建成功"); }
            setEditModal(false);
            form.resetFields();
            await loadRooms();
        } catch (e) {
            if (!e?.errorFields) message.error(e?.error || "操作失败");
        } finally {
            savingRoomRef.current = false;
            setSavingRoom(false);
        }
    };
    const handleDeleteRoom = async (id) => { try { await deleteRoom(id); message.success("删除成功"); loadRooms(); } catch (e) { message.error("删除失败"); } };
    const handleBatchList = () => {
        if (selectedRooms.length === 0) return message.warning("请选择房间");
        const selected = rooms.filter(room => selectedRooms.includes(room.id));
        const commissions = [...new Set(selected.map(room => room.commission).filter(value => value !== undefined && value !== null))];
        listForm.setFieldsValue({ commission: commissions.length === 1 ? commissions[0] : undefined });
        setListModal(true);
    };
    const confirmBatchList = async () => {
        try {
            const { commission } = await listForm.validateFields();
            await batchListRooms(selectedRooms, commission);
            message.success(`已上架 ${selectedRooms.length} 个房间，佣金 ¥${Number(commission).toFixed(2)}`);
            setListModal(false);
            listForm.resetFields();
            loadRooms();
        } catch (e) {
            if (!e?.errorFields) message.error(e?.error || "上架失败");
        }
    };
    const handleBatchUnlist = async () => { if (selectedRooms.length === 0) return message.warning("请选择房间"); try { await batchUnlistRooms(selectedRooms); message.success("下架成功"); loadRooms(); } catch (e) { message.error("下架失败"); } };
    const handleBatchDelete = async () => {
        if (selectedRooms.length === 0) return message.warning("请选择房间");
        Modal.confirm({
            title: "确认删除",
            content: `确定要删除选中的 ${selectedRooms.length} 个房间吗？此操作不可恢复！`,
            okText: "确认删除",
            okType: "danger",
            cancelText: "取消",
            onOk: async () => {
                try {
                    const token = localStorage.getItem("token");
                    await fetch("/api/rooms/batch-delete", {
                        method: "POST",
                        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
                        body: JSON.stringify({ room_ids: selectedRooms })
                    });
                    message.success("删除成功");
                    loadRooms();
                } catch (e) { message.error("删除失败"); }
            }
        });
    };
    const handleBatchCreate = async () => {
        if (creatingBatchRef.current) return;
        creatingBatchRef.current = true;
        setCreatingBatch(true);
        try {
            const values = await batchForm.validateFields();
            const start = Number(values.startRoom);
            const end = Number(values.endRoom);
            if (!Number.isInteger(start) || !Number.isInteger(end)) return message.error("起始和结束房号必须是整数");
            if (start > end) return message.error("起始房号不能大于结束房号");
            if (end - start + 1 > 50) return message.error("一次最多添加50个房间");
            const result = await batchCreateRooms({
                building_id: selectedBuilding,
                start_room: start,
                end_room: end,
                bedrooms: values.bedrooms,
                living_rooms: values.living_rooms || 0,
                rent: values.rent
            });
            if (result.created > 0 && result.skipped > 0) message.success(`新增${result.created}个，已跳过${result.skipped}个重复房号：${result.skipped_room_numbers.join("、")}`);
            else if (result.created > 0) message.success(`成功创建${result.created}个房间`);
            else message.warning("这些房号已经存在，没有重复创建");
            setBatchModal(false);
            batchForm.resetFields();
            await loadRooms();
        } catch (e) {
            if (!e?.errorFields) message.error(e?.error || "批量创建失败");
        } finally {
            creatingBatchRef.current = false;
            setCreatingBatch(false);
        }
    };

    const handleMarkRented = (room) => { setRentingRoom(room); rentForm.resetFields(); setRentModal(true); };
    const confirmMarkRented = async () => { try { const values = rentForm.getFieldsValue(); await updateRoom(rentingRoom.id, { status: "rented", tenant_name: values.tenant_name || "", tenant_phone: values.tenant_phone || "" }); message.success("已标记为已租，新入住需重新上传合同和居住登记"); setRentModal(false); loadRooms(); } catch (e) { message.error("操作失败"); } };
    const openRoomEditor = room => {
        setEditingRoom(room);
        form.setFieldsValue(room);
        setEditModal(true);
        loadContracts(room.id);
        loadResidences(room.id);
        loadRoomImages(room.id);
        loadRoomVideo(room.id);
    };

    const handleRequestVacant = (room) => {
        Modal.confirm({
            title: "发起退租通知", content: "确定要发起退租通知吗？通知发送后，需要租户确认后房间才会恢复为未租。", okText: "发送通知", cancelText: "取消",
            onOk: () => {
                Modal.confirm({
                    title: "再次确认", content: "点击确定后将正式发起退租通知，租户将收到退租通知。确定要继续吗？", okText: "确定发起", cancelText: "取消", okType: "danger",
                    onOk: async () => { try { const token = localStorage.getItem("token"); await fetch("/api/rooms/" + room.id + "/request-vacant", { method: "POST", headers: { "Content-Type": "application/json", Authorization: "Bearer " + token } }); message.success("退租通知已发送"); loadRooms(); } catch (e) { message.error("操作失败"); } }
                });
            }
        });
    };

    return (
        <div>
{vacantRequests.filter(r => r.status === "pending").length > 0 && (
    <Alert 
        message={`有 ${vacantRequests.filter(r => r.status === "pending").length} 个待确认的退租申请`} 
        type="warning" 
        showIcon 
        style={{ marginBottom: 16 }}
        description={
            <List 
                dataSource={vacantRequests.filter(r => r.status === "pending").slice(0, 3)}
                renderItem={item => (
                    <List.Item actions={[
                        <Button type="primary" size="small" onClick={() => handleConfirmVacant(item.id)}>确认退租</Button>
                    ]}>
                        <List.Item.Meta
                            title={`${item.tenant_name || "租户"} 申请退租 ${item.building_name} - ${item.room_number}`}
                            description={`申请时间：${new Date(item.created_at).toLocaleString()}`}
                        />
                    </List.Item>
                )}
            />
        }
    />
)}
{applications.filter(a => a.status === "pending").length > 0 && (
    <Alert 
        message={`有 ${applications.filter(a => a.status === "pending").length} 个待处理的租房申请`} 
        type="warning" 
        showIcon 
        style={{ marginBottom: 16 }}
        description={
            <List 
                dataSource={applications.filter(a => a.status === "pending").slice(0, 3)}
                renderItem={item => (
                    <List.Item actions={[
                        <Button type="primary" size="small" onClick={() => handleApproveApplication(item.id)}>同意</Button>,
                        <Button danger size="small" onClick={() => handleRejectApplication(item.id)}>拒绝</Button>
                    ]}>
                        <List.Item.Meta
                            title={`${item.applicant_name} 申请租住 ${item.building_name} - ${item.room_number}`}
                            description={`申请时间：${new Date(item.created_at).toLocaleString()}`}
                        />
                    </List.Item>
                )}
            />
        }
    />
)}
<Card className="room-manage-panel" title="房屋管理" extra={<Space className="room-manage-header-tools" wrap>
                <Select loading={buildingLoading} placeholder={buildingLoading ? "楼栋加载中" : (buildings.length === 0 ? "暂无楼栋" : "选择楼栋")} style={{ width: 200 }} value={selectedBuilding} onChange={setSelectedBuilding} disabled={buildingLoading || buildings.length === 0}>
                    {buildings.map(b => <Select.Option key={b.id} value={b.id}>{b.name}</Select.Option>)}
                </Select>
                {permissions.can_manage_rooms && <Button icon={<PlusOutlined />} onClick={() => { buildingForm.resetFields(); setSelectedRegion(null); setStreets([]); setBuildingModal(true); }}>添加楼栋</Button>}
                {applications.filter(a => a.status === "pending").length > 0 && <Button type="primary" icon={<FileTextOutlined />} onClick={() => setApplicationModal(true)}>租房申请 ({applications.filter(a => a.status === "pending").length})</Button>}
            </Space>}>
                {buildingLoading && buildings.length === 0 ? (
                    <PageLoadingOverlay caption="正在加载房屋信息，请稍候…" />
                ) : buildings.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "60px 0" }}><Alert message="暂无楼栋" description="请先添加楼栋" type="info" showIcon style={{ marginBottom: 16 }} />{permissions.can_manage_rooms && <Button type="primary" icon={<PlusOutlined />} size="large" onClick={() => { buildingForm.resetFields(); setSelectedRegion(null); setStreets([]); setBuildingModal(true); }}>添加楼栋</Button>}</div>
                ) : loading && rooms.length === 0 ? (
                    <PageLoadingOverlay caption="正在加载房间信息，请稍候…" />
                ) : (<div className="room-manage-loaded-content">
                    <div className="room-manage-toolbar" style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div className="room-status-legend">
                            <span><i style={{ background: "#52c41a" }} />未租</span>
                            <span><i style={{ background: "#ff4d4f" }} />已租</span>
                            <span><i style={{ background: "#8c8c8c" }} />已上架</span>
                        </div>
                        <Space className="room-batch-actions" wrap>
                            {permissions.can_publish && (<><Button type="primary" onClick={handleBatchList}>批量上架</Button><Button danger onClick={handleBatchUnlist}>批量下架</Button><Button danger icon={<DeleteOutlined />} onClick={handleBatchDelete}>批量删除</Button></>)}
                            {permissions.can_manage_rooms && (<><Button icon={<UnorderedListOutlined />} onClick={() => { if (checkBuildingExists()) { batchForm.resetFields(); setBatchModal(true); } }}>批量添加</Button><Button icon={<PlusOutlined />} onClick={() => { if (checkBuildingExists()) { form.resetFields(); setEditingRoom(null); setEditModal(true); } }}>添加房间</Button></>)}
                        </Space>
                    </div>
                    {activeRoom && permissions.can_manage_rooms && (
                        <div className="room-mobile-selected-actions">
                            <strong>房间 {activeRoom.room_number}</strong>
                            <div>
                                {activeRoom.status === "vacant" ? (
                                    <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => handleMarkRented(activeRoom)}>标记已租</Button>
                                ) : (
                                    <Button type="primary" icon={<UndoOutlined />} onClick={() => handleRequestVacant(activeRoom)}>发起退租</Button>
                                )}
                                <Button icon={<EditOutlined />} onClick={() => openRoomEditor(activeRoom)}>编辑</Button>
                                <Popconfirm title="确定删除这个房间吗？" onConfirm={() => handleDeleteRoom(activeRoom.id)}>
                                    <Button danger icon={<DeleteOutlined />}>删除</Button>
                                </Popconfirm>
                            </div>
                        </div>
                    )}
                    <div className="room-grid-zoom-hint">一行显示 12 个房间；手机可双指放大或缩小页面，点击房号后进行操作。</div>
                    <div className="room-manage-grid">
                        {rooms.map(room => (
                            <div key={room.id} className="room-manage-item">
                                <Tooltip title={<div><div>{room.room_number} - ¥{room.rent}/月</div>{room.is_listed && room.commission !== undefined && <div style={{ color: "#faad14", marginTop: 4 }}>中介佣金：¥{Number(room.commission).toFixed(2)}</div>}{room.status === "rented" && room.tenant_name && <div style={{ color: "#ff4d4f", marginTop: 4 }}>租户：{room.tenant_name}</div>}</div>}>
                                    <div className="room-manage-tile" onClick={() => { setActiveRoom(room); if (permissions.can_publish) setSelectedRooms(prev => prev.includes(room.id) ? prev.filter(id => id !== room.id) : [...prev, room.id]); }} style={{ background: getRoomColor(room), border: selectedRooms.includes(room.id) ? "3px solid #1890ff" : "none", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", position: "relative" }}>
                                        <div className="room-manage-number">{room.room_number}</div>
                                        {permissions.can_manage_rooms && (<div className="room-tile-actions-desktop" style={{ position: "absolute", top: 3, right: 3, display: "flex", gap: 4 }}>
                                            {room.status === "vacant" && <CheckCircleOutlined aria-label="标记已租" style={{ fontSize: 14, color: "#faad14" }} onClick={(e) => { e.stopPropagation(); handleMarkRented(room); }} />}
                                            {room.status === "rented" && <UndoOutlined aria-label="发起退租" style={{ fontSize: 14, color: "#fff" }} onClick={(e) => { e.stopPropagation(); handleRequestVacant(room); }} />}
                                            <EditOutlined aria-label="编辑房间" style={{ fontSize: 14 }} onClick={(e) => { e.stopPropagation(); openRoomEditor(room); }} />
                                            <Popconfirm title="确定删除?" onConfirm={() => handleDeleteRoom(room.id)}><DeleteOutlined aria-label="删除房间" style={{ fontSize: 14 }} onClick={(e) => e.stopPropagation()} /></Popconfirm>
                                        </div>)}
                                    </div>
                                </Tooltip>
                            </div>
                        ))}
                    </div>
                </div>)}
            </Card>

            <Modal title="租房申请" open={applicationModal} onCancel={() => setApplicationModal(false)} footer={null} width={600}>
                {applications.length === 0 ? <Empty description="暂无待处理的申请" /> : (
                    <List dataSource={applications.filter(a => a.status === "pending")} renderItem={item => (
                        <List.Item actions={[<Button type="primary" size="small" onClick={() => handleApproveApplication(item.id)}>同意</Button>, <Button danger size="small" onClick={() => handleRejectApplication(item.id)}>拒绝</Button>]}>
                            <List.Item.Meta avatar={<Avatar icon={<UserOutlined />} style={{ backgroundColor: "#1890ff" }} />} title={item.applicant_name} description={<div><p>申请租住：{item.building_name} - {item.room_number}</p><p>申请时间：{new Date(item.created_at).toLocaleString()}</p></div>} />
                        </List.Item>
                    )} />
                )}
            </Modal>

            <Modal title="标记为已租" open={rentModal} onOk={confirmMarkRented} onCancel={() => setRentModal(false)} okText="确认">
                <Form form={rentForm} layout="vertical">
                    <Space style={{ width: "100%" }}><Form.Item name="tenant_name" label="租户姓名" style={{ flex: 1 }}><Input placeholder="选填" /></Form.Item><Form.Item name="tenant_phone" label="租户电话" style={{ flex: 1 }}><Input placeholder="选填" /></Form.Item></Space>
                </Form>
            </Modal>

            <Modal title="批量添加房间" open={batchModal} onOk={handleBatchCreate} onCancel={() => { if (!creatingBatch) setBatchModal(false); }} confirmLoading={creatingBatch} cancelButtonProps={{ disabled: creatingBatch }} width={600}>
                <Form form={batchForm} layout="vertical">
                    <Space><Form.Item name="startRoom" label="起始房号"><Input placeholder="101" style={{ width: 120 }} /></Form.Item><Form.Item name="endRoom" label="结束房号"><Input placeholder="108" style={{ width: 120 }} /></Form.Item></Space>
                    <Divider />
                    <Space><Form.Item name="bedrooms" label="卧室数"><InputNumber min={0} style={{ width: 80 }} /></Form.Item><Form.Item name="living_rooms" label="客厅数"><InputNumber min={0} style={{ width: 58 }} /></Form.Item><Form.Item name="rent" label="租金"><InputNumber min={0} style={{ width: 120 }} /></Form.Item></Space>
                </Form>
            </Modal>

            <Modal title="上架到公共池" open={listModal} onOk={confirmBatchList} onCancel={() => setListModal(false)} okText="确认上架">
                <Alert type="info" showIcon message={`已选择 ${selectedRooms.length} 个房间`} description="佣金只有该房源所属房东和中介账号可以查看。" style={{ marginBottom: 16 }} />
                <Form form={listForm} layout="vertical">
                    <Form.Item name="commission" label="中介佣金" rules={[{ required: true, message: "请输入中介佣金" }]}>
                        <InputNumber min={0} max={1000000} precision={2} prefix="¥" addonAfter="元" style={{ width: "100%" }} placeholder="例如：1500" />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal title={<h2 style={sectionTitleStyle}>{editingRoom ? "编辑房间" : "添加房间"}</h2>} open={editModal} onOk={handleSaveRoom} onCancel={() => { if (!savingRoom) setEditModal(false); }} confirmLoading={savingRoom} cancelButtonProps={{ disabled: savingRoom }} width={680} styles={{ body: { maxHeight: "72vh", overflowY: "auto", paddingRight: 6 } }}>
                  <Form form={form} layout="vertical">
                      <Row gutter={[12, 0]}>
                          <Col xs={24} sm={8}><Form.Item name="room_number" label="房号" rules={[{ required: true, message: "请输入房号" }]}><Input placeholder="例如：101" /></Form.Item></Col>
                          <Col xs={12} sm={8}><Form.Item name="bedrooms" label="卧室数" rules={[{ required: true, message: "请输入卧室数" }]}><InputNumber min={0} precision={0} style={{ width: "100%" }} /></Form.Item></Col>
                          <Col xs={12} sm={8}><Form.Item name="living_rooms" label="客厅数"><InputNumber min={0} precision={0} style={{ width: "100%" }} /></Form.Item></Col>
                          <Col xs={24} sm={12}><Form.Item name="rent" label="月租金" rules={[{ required: true, message: "请输入租金" }]}><InputNumber min={0} max={1000000} precision={2} prefix="¥" addonAfter="元/月" style={{ width: "100%" }} /></Form.Item></Col>
                          <Col xs={24} sm={12}><Form.Item name="decoration" label="装修情况"><Select allowClear placeholder="请选择装修情况" options={[{ value: "simple", label: "简装" }, { value: "apartment", label: "公寓" }, { value: "community", label: "小区" }]} /></Form.Item></Col>
                      </Row>
                      <Divider orientation="left" plain>公共池费用信息</Divider>
                      <Row gutter={[12, 0]}>
                          <Col xs={12} sm={8}><Form.Item name="water_price" label="水费"><InputNumber min={0} max={10000} precision={2} prefix="¥" addonAfter="元/方" style={{ width: "100%" }} /></Form.Item></Col>
                          <Col xs={12} sm={8}><Form.Item name="electricity_price" label="电费"><InputNumber min={0} max={10000} precision={2} prefix="¥" addonAfter="元/度" style={{ width: "100%" }} /></Form.Item></Col>
                          <Col xs={24} sm={8}><Form.Item name="management_fee" label="管理费"><InputNumber min={0} max={100000} precision={2} prefix="¥" addonAfter="元/月" style={{ width: "100%" }} /></Form.Item></Col>
                          <Col xs={24} sm={12}><Form.Item name="deposit_method" label="押金方式（选填）"><Select allowClear placeholder="请选择" options={[{ value: "one_for_one", label: "押一付一" }, { value: "two_for_one", label: "押二付一" }, { value: "other", label: "其他" }]} /></Form.Item></Col>
                          <Form.Item noStyle shouldUpdate={(prev, current) => prev.deposit_method !== current.deposit_method}>
                              {({ getFieldValue }) => getFieldValue("deposit_method") === "other" ? (
                                  <Col xs={24} sm={12}><Form.Item name="deposit_method_other" label="其他押金方式" rules={[{ required: true, message: "请输入其他押金方式" }, { max: 15, message: "最多输入15个字" }]}><Input maxLength={15} showCount placeholder="最多15个字" /></Form.Item></Col>
                              ) : null}
                          </Form.Item>
                      </Row>
                  </Form>
                  {editingRoom && (
                      <div style={{ marginTop: 16 }}>
                          <h4>房间图片</h4>
                          <div style={{ marginBottom: 16 }}>
                              <input type="file" onChange={handleImageUpload} accept=".jpg,.jpeg,.png" disabled={imageUploading} />
                              {imageUploading && <span style={{ marginLeft: 8 }}>上传中...</span>}
                          </div>
                          {roomImages.length > 0 && (
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                  {roomImages.map(img => (
                                      <div key={img.id} style={{ position: "relative" }}>
                                          <img src={img.file_url} alt="房间图片" style={{ width: 100, height: 100, objectFit: "cover", borderRadius: 4 }} />
                                          <Popconfirm title="确定删除？" onConfirm={() => handleDeleteImage(img.id)}>
                                              <Button type="text" danger size="small" style={{ position: "absolute", top: 0, right: 0 }}>X</Button>
                                          </Popconfirm>
                                      </div>
                                  ))}
                              </div>
                          )}
                          <div style={{ marginTop: 24 }}>
                              <h4>房间视频（最多60秒）</h4>
                              <div style={{ marginBottom: 16 }}>
                                  <input type="file" onChange={handleVideoUpload} accept="video/*" disabled={videoUploading} />
                                  {videoUploading && <span style={{ marginLeft: 8 }}>上传中...</span>}
                              </div>
                              {roomVideo && (
                                  <div style={{ position: "relative" }}>
                                      <video src={roomVideo.file_url} controls style={{ width: 300, height: 200, borderRadius: 4 }} />
                                      <Popconfirm title="确定删除视频？" onConfirm={handleDeleteVideo}>
                                          <Button type="text" danger size="small" style={{ position: "absolute", top: 0, right: 0 }}>删除</Button>
                                      </Popconfirm>
                                  </div>
                              )}
                          </div>
                      </div>
                  )}
                  {editingRoom && user.role === "landlord" && editingRoom.status === "rented" && (
                      <div style={{ marginTop: 32 }}>
                          <h2 style={sectionTitleStyle}>合同管理</h2>
                          <Form form={contractForm} layout="vertical" style={{ marginBottom: 16 }}>
                              <Form.Item name="title" label="合同标题" rules={[{ required: true }]}>
                                  <Input placeholder="例如：租赁合同" />
                              </Form.Item>
                              <Form.Item name="file" label="合同文件">
                                  <input type="file" onChange={handleFileChange} accept=".jpg,.jpeg,.png,.pdf,.xlsx,.xls" style={{ marginBottom: 8 }} />
                                  {selectedFile && <span style={{ color: "#1890ff" }}>{selectedFile.name}</span>}
                              </Form.Item>
                              <Form.Item>
                                  <Button type="primary" onClick={handleUploadContract}>上传合同</Button>
                              </Form.Item>
                          </Form>
                          {contracts.length > 0 && (
                              <div style={{ marginTop: 16 }}>
                                  <h4>已上传的合同</h4>
                                  <ProtectedImagePreviewGroup preview={{ mask: "点击放大" }}>
                                  <List size="small" dataSource={contracts} renderItem={item => (
                                      <List.Item actions={[
                                          <Popconfirm title="确定删除？" onConfirm={() => handleDeleteContract(item.id)}>
                                              <Button type="text" danger size="small">删除</Button>
                                          </Popconfirm>
                                      ]}>
                                          <List.Item.Meta
                                              avatar={<FileTextOutlined />}
                                              title={item.title}
                                              description={
                                                  <div>
                                                      {item.file_url && (
                                                          <div style={{ marginTop: 8 }}>
                                                              {item.file_type && item.file_type.startsWith("image/") ? (
                                                                  <ProtectedImage document={item} alt={item.title || "合同图片"} style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 4 }} preview />
                                                              ) : (
                                                                  <><FileTextOutlined /> 已上传文件</>
                                                              )}
                                                              <div style={{ marginTop: 4 }}>
                                                                  <ProtectedFileLink document={item}>下载文件</ProtectedFileLink>
                                                              </div>
                                                          </div>
                                                      )}
                                                      <div style={{ color: "#8c8c8c", fontSize: 12, marginTop: 4 }}>
                                                          上传时间：{new Date(item.created_at).toLocaleString()}
                                                      </div>
                                                  </div>
                                              }
                                          />
                                      </List.Item>
                                  )} />
                                  </ProtectedImagePreviewGroup>
                              </div>
                          )}
                      </div>
                  )}
                  {editingRoom && user.role === "landlord" && editingRoom.status === "rented" && residences.length > 0 && (
                      <div style={{ marginTop: 16, borderTop: "1px solid #f0f0f0", paddingTop: 16 }}>
                          <h4>居住登记</h4>
                          <ProtectedImagePreviewGroup preview={{ mask: "点击放大" }}>
                          <List size="small" dataSource={residences} renderItem={item => (
                              <List.Item actions={[
                                  <Popconfirm title="确定删除？" onConfirm={() => handleDeleteResidence(item.id)}>
                                      <Button type="text" danger size="small">删除</Button>
                                  </Popconfirm>
                              ]}>
                                  <List.Item.Meta
                                      avatar={<UserOutlined />}
                                      title={item.name}
                                      description={
                                          <div>
                                              {item.phone && <p>电话：{item.phone}</p>}
                                              {item.id_card && <p>身份证号：{item.id_card}</p>}
                                              {item.file_url && (
                                                  <div style={{ marginTop: 8 }}>
                                                      <ProtectedImage document={item} alt="身份证照片" style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 4 }} preview />
                                                  </div>
                                              )}
                                              <p style={{ color: "#8c8c8c", fontSize: 12 }}>上传人：{item.uploader_name} ({item.uploader_role === "landlord" ? "房东" : "住户"})</p>
                                              <p style={{ color: "#8c8c8c", fontSize: 12 }}>上传时间：{new Date(item.created_at).toLocaleString()}</p>
                                          </div>
                                      }
                                  />
                              </List.Item>
                          )} />
                          </ProtectedImagePreviewGroup>
                      </div>
                  )}
              </Modal>

            <Modal title="添加楼栋" open={buildingModal} onOk={handleCreateBuilding} onCancel={() => { if (!creatingBuilding) setBuildingModal(false); }} confirmLoading={creatingBuilding} cancelButtonProps={{ disabled: creatingBuilding }} width={720}>
                <Form form={buildingForm} layout="vertical">
                    <Form.Item name="name" label="楼栋名称" rules={[{ required: true, whitespace: true, message: "请输入楼栋名称" }]}><Input placeholder="例如：金德居" /></Form.Item>
                    <Form.Item label="所属区域" required><Select value={selectedRegion} onChange={setSelectedRegion} placeholder="请选择区域">{regions.map(r => <Select.Option key={r.id} value={r.id}>{r.name}</Select.Option>)}</Select></Form.Item>
                    <Form.Item name="street_id" label="所属街道" rules={[{ required: true, message: "请选择所属街道" }]}><Select disabled={!selectedRegion} placeholder="请选择街道">{streets.map(s => <Select.Option key={s.id} value={s.id}>{s.name}</Select.Option>)}</Select></Form.Item>
                    <Form.Item name="address" label="详细地址"><Input placeholder="选填，例如：XX路XX号" /></Form.Item>
                    <BuildingLocationPicker form={buildingForm} />
                </Form>
            </Modal>
        </div>
    );
};

export default RoomManageScreen;




























